// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn} from '@angular/forms';
import {FormsModule} from '@angular/forms';

// RxJS
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable, Subject, throwError} from 'rxjs';
import {debounceTime, take} from 'rxjs/operators';

// UI
import {
  AlertComponent,
  ButtonComponent,
  DividerComponent,
  InputDirective,
  InputGroupComponent,
  InputHintComponent,
  InputLabelComponent,
  LabelComponent,
  ModalService as UiModalService,
  SkeletonComponent,
  TabComponent,
  TabsComponent,
  TabTextDirective,
  TextareaComponent,
  TooltipDirective,
} from '@sms-ir/ui-components';

// App
import {FormErrorDirective} from '@shared/directives/form-error.directive';
import type {ApiResponse, PaginatedResult} from '@app/core/api';
import type {BootstrapResult} from '@app/core/interfaces';
import {XmaApiService} from '@app/core/services/xma-api.service';
import {UserTemplateApiService} from '@app/main/modules/template/api/user-template-api.service';
import type {UserTemplateResult} from '@app/main/modules/template/api/user-template-api.models';
import {TextGenerationModalComponent} from '@shared/components/text-generation-modal/text-generation-modal.component';
import {AppNotifications} from '@shared/enums/app-notifications.enum';
import {StoreService} from '@shared/services/store.service';
import {ToastifyService} from '@shared/services/toastify.service';
import {UtilityService} from '@shared/services/utility.service';
import {ValidatorsService} from '@shared/services/validators.service';
import {FormValidationHelperService} from '@shared/services/form-validation-helper.service';
import {compactQuery} from '@shared/utils/compact-query';

// Feature
import {TextBuilderTypeEnum} from './models/text-builder-type.enum';
import {TextBuilderService} from './services/text-builder.service';


@UntilDestroy()
@Component({
    selector: 'app-text-builder',
    templateUrl: './text-builder.component.html',
    styleUrls: ['./text-builder.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
      '[attr.title]': 'null',
    },
    animations: [trigger('fadeSlideOut', [
            transition(':leave', [
                animate('800ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateY(10px)' }))
            ])
        ])],
    standalone: true,
    imports: [FormErrorDirective, 
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      AlertComponent,
      ButtonComponent,
      DividerComponent,
      InputDirective,
      InputGroupComponent,
      InputLabelComponent,
      InputHintComponent,
      LabelComponent,
      SkeletonComponent,
      TabsComponent,
      TabComponent,
      TabTextDirective,
      TooltipDirective,
      TextareaComponent,
    ],
})
export class TextBuilderComponent implements OnInit, OnChanges {
  hasDoubleChar = true;
  isMaxLenValid = true;
  isMobile: boolean = false;
  isSmallScreen: boolean = false;
  container = 70;
  totalSize = 932;
  localSmsLen = 0;
  smsCount = 0;
  drafts!: UserTemplateResult[];
  templates!: UserTemplateResult[];
  remainedForbiddenWords: string[] = [];
  activeTab = 'templates';
  form!: FormGroup;
  formURL!: FormGroup;
  errorMsg: any = null;
  @ViewChild('message', {read: ElementRef}) message?: ElementRef<HTMLTextAreaElement>;
  private savedSelection = {start: 0, end: 0};
  private hasSavedSelection = false;
  @Input() title: string = "لطفاً متن پیامک خود را وارد کنید."
  @Input() showCounter: boolean= true;
  @Input() selectedTemplateId!: number;
  @Input() messageText!: Observable<string> | string;
  @Input() linkURL!: Observable<string>;
  @Input() forbiddenWords: string[] = [];
  @Input() textBuilderType!: TextBuilderTypeEnum;
  @Input() inputParameters!: string[];
  @Input() heightSize?: string
  @Input() optionalField?: boolean
  @Input() isURL = false;
  @Input() cancellationText!: string;
  @Input() hasTransparentBackground: boolean = false;
  @Input() defaultActiveTab: 'templates' | 'personalize' = 'templates';
  @Input() alertMessage?: string;
  @Input() validators: ValidatorFn[] = [];
  @Output() draftLoaded: EventEmitter<UserTemplateResult> = new EventEmitter<UserTemplateResult>();
  @Output() maxLenValidated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() messageTextChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() messageURL: EventEmitter<string> = new EventEmitter<string>();
  searchValueSubscription: Subject<string> = new Subject<string>();
  textBuilderTypeEnum = TextBuilderTypeEnum;
  parameters!: { parameter: string, title: string; }[];
  isShortLink = true;
  bootstrap$: Observable<BootstrapResult | null>;
  cancellation!: string;
  public minLength = 15;
  public errorcontent: string = ` حداقل تعداد کاراکتر مجاز برای استفاده از بهبود متن و شکلک گذاری ${this.minLength}کاراکتر است. `
  originalText: string = '';
  updatedText: string = '';
  isTextChanging: boolean = false;
  messages = '';
  isTextEntered: any;
  showFloatingActions = false;
  showXmaTooltip = true;
  validationAttempted = false;

  constructor(
    private _UserTemplateService: UserTemplateApiService,
    private _XmaService: XmaApiService,
    private utilityService: UtilityService,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private validatorsService: ValidatorsService,
    private storeService: StoreService,
    private toast: ToastifyService,
    private textBuilderService: TextBuilderService,
    private uiModal: UiModalService,
    private formValidationHelper: FormValidationHelperService,
  ) {

    this.bootstrap$ = this.storeService.baseData$;
    this.parameters = this.textBuilderService.parameters;
  }

  ngOnInit(): void {
    this.updateDeviceType();


    window.addEventListener('resize', () => {
      this.updateDeviceType();
    });

    const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)\/?$/i;
    this.form = this.formBuilder.group({
      text: new FormControl('', [this.validatorsService.required(), ...this.validators]),
    });
    this.formURL = this.formBuilder.group({
      webURL: new FormControl('', [this.validatorsService.pattern(urlPattern)]),
    });

    this.initializeTextBuilder();
    if (this.optionalField === true) {
      this.form.get('text')?.setValidators(null)
    }
    if (this.messageText instanceof Observable) {
      this.messageText.pipe(untilDestroyed(this)).subscribe(res => {
        const persianVersion = this.textBuilderService.convertToPersian(res ? res : '');
        this.form.controls.text.patchValue(persianVersion);
      });
    } else {
      const persianVersion = this.textBuilderService.convertToPersian(this.messageText ? this.messageText : '');
      this.form.controls.text.patchValue(persianVersion);
    }
    if (this.isURL) {
      this.linkURL
        .pipe(untilDestroyed(this))
        .subscribe(res => {
          const normalized = this.normalizeWebUrl(res === '' ? `https://${res}` : res);
          this.formURL.controls.webURL.patchValue(normalized);
          this.isShortLink = (normalized === '' || normalized === 'https://');
        });
    }
    this.remainedForbiddenWords = this.forbiddenWords;
    this.onMessageTextChange();
    if (
      this.textBuilderType === this.textBuilderTypeEnum.Template ||
      this.textBuilderType === this.textBuilderTypeEnum.TemplateAndParametric
    ) {
      this.activeTab = this.defaultActiveTab;
    } else if (this.textBuilderType === this.textBuilderTypeEnum.Parametric) {
      this.activeTab = 'personalize';
    }

  }

  @HostListener('window:resize', [])
  onResize() {
    this.updateDeviceType();
  }

  updateDeviceType() {
    this.isMobile = window.innerWidth <= 768;
    this.isSmallScreen = window.innerWidth <= 425;
    this.changeDetectorRef.markForCheck();
  }

  get displayParameters(): { parameter: string; title: string }[] {
    if (!this.parameters?.length) {
      return [];
    }
    // Short-link chip is only for flows that enable URL personalization (isURL).
    return this.isURL
      ? this.parameters
      : this.parameters.filter((parameter) => parameter.parameter !== 'link');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.form) return;

    if (changes && changes.cancellationText && changes.cancellationText.previousValue !== changes.cancellationText.currentValue) {
      this.syncCancellationLabel();
      this.onMessageTextChange();
    }
  }

  importURL(e: any) {
    if (e.target.value === '' || e.target.value === 'https://') {
      this.isShortLink = true;
      let expression = '';
      if (!this.isMaxLenValid) {
        return;
      }
      expression = `#link#`;
      this.form.controls.text.patchValue(this.form.controls.text.value.replaceAll(` ${expression}`, ''));
      this.messageURL.emit(e.target.value);
      this.onMessageTextChange();
    } else {
      this.isShortLink = true;
      let expression = '';
      if (!this.isMaxLenValid) {
        return;
      }
      expression = `#link#`;
      this.form.controls.text.patchValue(this.form.controls.text.value.replaceAll(` ${expression}`, ''));
      this.onMessageTextChange();
    }
  }

  generateURL() {
    this.isShortLink = false;
    const normalized = this.normalizeWebUrl(this.formURL.get('webURL')?.value);
    this.formURL.controls.webURL.patchValue(normalized, {emitEvent: false});
    this.messageURL.emit(normalized);
  }

  private normalizeWebUrl(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  initializeTextBuilder() {
    switch (this.textBuilderType) {
      case TextBuilderTypeEnum.Template:
      case TextBuilderTypeEnum.TemplateAndParametric:
        this.getAllDrafts();
        this.searchValueSubscription
          .pipe(debounceTime(1000))
          .subscribe((res) => {
            this.filterDesiredData(res);
          });
        break;
      case TextBuilderTypeEnum.Textarea:
        break;
      case TextBuilderTypeEnum.Parametric:
        if (this.inputParameters.length !== 0) {
          this.parameters = [];
          this.inputParameters.forEach(el => {
            this.parameters = [...this.parameters, {parameter: el, title: el}];
          });
        } else {
          throwError('parametric text-builder doesnt have any parameters');
        }
        break;
      case TextBuilderTypeEnum.FastSend:
        break;
      default:
        this.textBuilderType = this.textBuilderTypeEnum.Template;
        break;
    }
  }

  checkDoubleChar() {
    let data = this.form.controls.text.value;

    if (data == "" || data == null) return;
    this.hasDoubleChar = this.utilityService.containsNonLatinCodepoints(this.form.controls.text.value);
  }

  onMessageTextChange() {
    this.checkDoubleChar();
    this.totalSize = this.hasDoubleChar ? 932 : 2136;
    const repeatingContainerSize = this.hasDoubleChar ? 67 : 153;
    const firstContainerSize = this.hasDoubleChar ? 70 : 160;
    const secondContainerSize = this.hasDoubleChar ? 134 : 306;
    const thirdContainerSize = this.hasDoubleChar ? 201 : 459;
    this.syncCancellationLabel();
    const textValue = this.form.controls.text.value ?? '';
    let len = textValue.length;
    if (this.cancellation) {
      len = textValue.length + this.cancellation.length + 1;
    }
    this.container = repeatingContainerSize;

    if (len === 0) {
      this.smsCount = 0;
      this.container = firstContainerSize;
      this.localSmsLen = len;
    } else if (len <= firstContainerSize) {
      this.smsCount = 1;
      this.container = firstContainerSize;
      this.localSmsLen = len;
    } else if (len > firstContainerSize && len <= secondContainerSize) {
      this.smsCount = 2;
      this.container = secondContainerSize - firstContainerSize;
      this.localSmsLen = len - firstContainerSize;
    } else if (len > secondContainerSize && len <= thirdContainerSize) {
      this.smsCount = 3;
      this.localSmsLen = len - secondContainerSize;
    } else {
      this.smsCount = 4 + Math.floor((len - thirdContainerSize - 1) / repeatingContainerSize);
      this.localSmsLen = len - (thirdContainerSize + ((this.smsCount - 4) * repeatingContainerSize));
    }
    const p = this.textBuilderService.convertToEnglish(textValue);
    this.messageTextChanged.emit(p);
    this.maxLenValidating()

    const text = textValue;
    this.isTextEntered = !!text;

    if (!this.originalText) {
      this.originalText = this.form.get('text')?.value || '';
    }

    if (this.showFloatingActions && this.updatedText !== text) {
      this.showFloatingActions = false;
    }

    this.changeDetectorRef.markForCheck();
  }

  private syncCancellationLabel(): void {
    if (this.cancellationText) {
      this.cancellation = this.hasDoubleChar
        ? `لغو${this.toPersianDigits(this.cancellationText)}`
        : `OFF${this.cancellationText}`;
    } else {
      this.cancellation = '';
    }
  }

  private toPersianDigits(value: string): string {
    return String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
  }

  saveSelection(): void {
    const el = this.message?.nativeElement;
    // On blur browsers often reset selection to 0 — never overwrite a good caret then.
    if (!el || document.activeElement !== el) {
      return;
    }
    this.savedSelection = {
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? el.selectionStart ?? 0,
    };
    this.hasSavedSelection = true;
  }

  onPersonalizeMouseDown(event: MouseEvent, parameter: { title: string; parameter: string }): void {
    // Keep textarea focus/selection so the token is inserted at the caret, not at 0 / next line.
    event.preventDefault();
    this.addSegment(parameter);
  }

  addSegment(parameter: { title: string, parameter: string }): void {
    if (!this.isMaxLenValid) {
      return;
    }

    const el = this.message?.nativeElement;
    const current = this.form.controls.text.value ?? '';
    const expression = `#${parameter.title}#`;
    const hasFocus = !!el && document.activeElement === el;

    let start: number;
    let end: number;

    if (hasFocus) {
      start = el.selectionStart ?? current.length;
      end = el.selectionEnd ?? start;
    } else if (this.hasSavedSelection) {
      start = this.savedSelection.start;
      end = this.savedSelection.end;
    } else {
      start = current.length;
      end = current.length;
    }

    if (
      start == null ||
      end == null ||
      start < 0 ||
      end < 0 ||
      start > current.length ||
      end > current.length ||
      start > end
    ) {
      start = current.length;
      end = current.length;
    }

    const prevCharIsSpace = current[start - 1] === undefined || current[start - 1] === ' ';
    const nextCharIsSpace = current[end] === undefined || current[end] === ' ';
    const prefix = prevCharIsSpace ? '' : ' ';
    const suffix = nextCharIsSpace ? '' : ' ';
    const mainString =
      `${current.substring(0, start)}${prefix}${expression}${suffix}${current.substring(end)}`;

    const selectionRange = start + prefix.length + expression.length;
    this.form.controls.text.patchValue(mainString);
    this.savedSelection = {start: selectionRange, end: selectionRange};
    this.hasSavedSelection = true;

    this.showFloatingActions = false;
    this.onMessageTextChange();

    queueMicrotask(() => {
      const textarea = this.message?.nativeElement;
      if (!textarea) {
        return;
      }
      textarea.focus({preventScroll: true});
      textarea.setSelectionRange(selectionRange, selectionRange);
      this.savedSelection = {start: selectionRange, end: selectionRange};
      this.hasSavedSelection = true;
    });
  }

  maxLenValidating() {
    if (this.hasDoubleChar) {
      this.isMaxLenValid = this.form.controls.text.value.length <= 932;
    } else {
      this.isMaxLenValid = this.form.controls.text.value.length <= 2136;
    }
    this.setMaxErrorLength();
    this.maxLenValidated.emit(this.isMaxLenValid);
  }

  setMaxErrorLength() {
    const control = this.form.controls.text;

    if (!this.isMaxLenValid) {
      control.setErrors({...(control.errors ?? {}), maxLength: {valid: false}});
      return;
    }

    if (!control.errors?.['maxLength']) {
      return;
    }

    const {maxLength, ...rest} = control.errors;
    control.setErrors(Object.keys(rest).length ? rest : null);
    control.updateValueAndValidity({onlySelf: true, emitEvent: false});
  }

  getAllDrafts() {
    const params = compactQuery({
      pageNumber: 1, pageSize: 1000,
    })
    this._UserTemplateService.getUserTemplates(params)
      .subscribe((res: ApiResponse<PaginatedResult<UserTemplateResult>>) => {
        if (this.selectedTemplateId) {
          this.drafts = res.result.items.filter(i => i.id !== this.selectedTemplateId);
          this.templates = this.drafts;
          this.changeDetectorRef.markForCheck();
        } else {
          this.drafts = res.result.items;
          this.templates = res.result.items;
          this.changeDetectorRef.markForCheck();
        }

      });
  }

  selectTemplate(draft: UserTemplateResult) {
    this.form.controls.text.patchValue(this.textBuilderService.convertToPersian(draft.messageText));
    this.showFloatingActions = false;
    this.draftLoaded.emit(draft);
    this.onMessageTextChange();
    this.message?.nativeElement.focus({preventScroll: true})
  }

  switchTab(mode: string | number) {
    this.activeTab = String(mode);
  }

  onInput($event: any) {
    this.searchValueSubscription.next($event.target.value);
  }

  filterDesiredData(phrase: string) {
    this.templates = this.drafts.filter(el => (el.title.includes(phrase)) ||
      (el.title.includes(phrase.toLowerCase()) ||
        (el.title.includes(phrase.toUpperCase()))));
    this.changeDetectorRef.markForCheck();
  }

  errorStatusChanged($event: string | null) {
    if ($event !== null) {
      this.errorMsg = $event;
    } else {
      this.errorMsg = undefined;
    }
  }

  openTextGenerationModal(): void {
    const currentText = this.form.controls.text.value || '';
    this.uiModal.open(TextGenerationModalComponent, {
      size: '2xl',
      mask: true,
      maskClosable: true,
      keyboard: true,
      ariaLabel: 'دستیار هوشمند ایکسما',
      data: {
        currentText,
        minLength: this.minLength,
      },
    }).afterClosed$.pipe(take(1)).subscribe((result) => {
      if (result && typeof result === 'object' && result.action === 'replace') {
        this.form.controls.text.setValue(result.text);
        this.showFloatingActions = false;
        this.onMessageTextChange();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  emojifyText() {
    if (this.form.controls.text.value.length < this.minLength) {
      this.toast.error(this.errorcontent);
      return;
    }

    this.isTextChanging = true;
    this.changeDetectorRef.markForCheck();
    this.originalText = this.form.controls.text.value;
    const body = {text: this.originalText};
    this._XmaService.textEmojify(body).subscribe({
      next: (response: any) => {
        this.isTextChanging = false;
        if (response.success) {
          this.updateFormText(response.result);
          this.showFloatingActions = true;
        }
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        this.onMessageTextChange();
        this.isTextChanging = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  acceptChanges(): void {
    this.showFloatingActions = false;
    this.originalText = this.form.controls.text.value;
    this.toast.success(AppNotifications.ChangesSaved);
    this.changeDetectorRef.markForCheck();
  }

  rejectChanges(): void {
    this.form.controls.text.setValue(this.originalText);
    this.onMessageTextChange();
    this.showFloatingActions = false;
    this.changeDetectorRef.markForCheck();
  }

  updateFormText(newValue: string): void {
    this.updatedText = newValue;
    this.form.controls.text.setValue(newValue);
    this.onMessageTextChange();
    this.changeDetectorRef.markForCheck();
  }

  showValidationErrors(): void {
    this.validateAndScroll();
  }

  validateAndScroll(): boolean {
    if (!this.form) {
      return true;
    }

    this.validationAttempted = true;
    const valid = this.formValidationHelper.validateAndScroll(this.form);
    this.changeDetectorRef.markForCheck();
    return valid;
  }

}
